import React, { useState, useEffect, useRef } from 'react'
import {
  Plus,
  Download,
  Users,
  CheckCircle,
  RotateCcw,
  ArrowRightLeft
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import assetAssignmentService from '../../services/assetAssignmentService'
import { formatDate, statusBadgeClass } from './assetUtils'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const AssetAssignments = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalAssignments: 0,
    assignedCount: 0,
    returnedCount: 0,
    transferredCount: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [listResponse, statsResponse] = await Promise.all([
        assetAssignmentService.getAllAssignments({ limit: 1000 }),
        assetAssignmentService.getAssignmentStats()
      ])

      if (listResponse.success) {
        // Flatten related master names so the table can search and sort on them
        const transformedData = (listResponse.data || []).map(item => ({
          ...item,
          id: item._id,
          locationName: item.location?.locationName || ''
        }))
        setData(transformedData)
        if (listResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No assignment records found. Create your first assignment!')
        }
      } else {
        toast.error(listResponse.message || 'Failed to load asset assignments')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Error loading asset assignments:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view asset assignments')
      } else if (!error._errorHandled) {
        toast.error('Failed to load asset assignment records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'assignmentCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'assetName',
      header: 'Asset',
      cell: ({ row }) => {
        const acquisition = row.original.acquisition
        const name = row.original.assetName || acquisition?.assetName || acquisition?.acquisitionCode
        return (
          <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
            <span className="description-text">{name || '-'}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'assetTag',
      header: 'Asset Tag',
      cell: ({ row }) => (
        <div style={{ textAlign: 'left' }}>
          {row.original.assetTag || row.original.acquisition?.assetTag || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'assignedToName',
      header: 'Assigned To',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{getValue() || '-'}</div>
      ),
    },
    {
      accessorKey: 'department',
      header: 'Department',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{getValue() || '-'}</div>
      ),
    },
    {
      accessorKey: 'locationName',
      header: 'Location',
      cell: ({ row }) => {
        const location = row.original.location
        return (
          <div style={{ textAlign: 'left' }}>
            {location ? (location.locationName || location.locationCode || '-') : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'assignmentDate',
      header: 'Assigned On',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{formatDate(getValue())}</div>
      ),
    },
    {
      accessorKey: 'returnDate',
      header: 'Return Date',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{formatDate(getValue())}</div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${statusBadgeClass(getValue())}`}>{getValue() || '-'}</span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/assets/assignments/form')
  }

  const handleEdit = (record) => {
    navigate(`/assets/assignments/edit?id=${record._id}`, { state: { assignmentData: record } })
  }

  const handleView = (record) => {
    navigate(`/assets/assignments/view?id=${record._id}`, { state: { assignmentData: record } })
  }

  const handleDelete = (record) => {
    setDeleteItem(record)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting assignment...')
      const response = await assetAssignmentService.deleteAssignment(deleteItem._id)
      if (response.success) {
        toast.success('Assignment deleted successfully!', { autoClose: 2000 })
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete assignment')
      }
    } catch (error) {
      console.error('Error deleting assignment:', error)
      if (error.response?.status === 404) {
        toast.error('Assignment not found')
      } else if (!error._errorHandled) {
        toast.error('Failed to delete assignment. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Assets', path: '/assets' },
          { label: 'Assignments', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Asset Assignments</h1>
          <p>Asset assignments and transfers</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Assignment
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalAssignments || 0}</div>
          <div className="metric-title">Total Assignments</div>
          <div className="metric-subtitle">All records</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.assignedCount || 0}</div>
          <div className="metric-title">Currently Assigned</div>
          <div className="metric-subtitle">In use by staff</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <RotateCcw size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Returned</span>
            </div>
          </div>
          <div className="metric-value">{stats.returnedCount || 0}</div>
          <div className="metric-title">Returned</div>
          <div className="metric-subtitle">Back in stock</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <ArrowRightLeft size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Moved</span>
            </div>
          </div>
          <div className="metric-value">{stats.transferredCount || 0}</div>
          <div className="metric-title">Transferred</div>
          <div className="metric-subtitle">Moved between users</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          searchable={true}
          searchPlaceholder="Search assignments..."
          noCard={true}
          pageSize={10}
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Assignment"
        message={`Are you sure you want to delete assignment "${deleteItem?.assignmentCode}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default AssetAssignments
